import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  describeImage,
  generateImage,
  listChapterImages,
  placeImage,
  setChapterText,
  updateImage,
  uploadImage,
  cropImage,
  type ChapterImage,
  type ChapterImages,
} from "../api";
import MutationError from "../components/MutationError";
import { useT } from "../i18n";
import {
  afterSave,
  applyFields,
  fieldsOf,
  isDirty,
  locate,
  pictureSlot,
  restore,
  snapshotOf,
  type Field,
} from "./fields";
import { paragraphsOf, TOP } from "./paragraphs";
import { useGrowToText } from "./useGrowToText";
import PictureSheet from "./PictureSheet";
import IllustrateChapter from "./IllustrateChapter";
import { CastNotices, Inserter, Placed, UnplacedPictures, type Run } from "./PictureParts";
import { contextOf, place, type Placement } from "./placement";
import RewriteSheet from "./RewriteSheet";
import { fieldAtTop, showField, type ViewAnchor } from "./viewport";

/** How the mode was opened: from a line, to rewrite it, or where the author was. */
export interface EditStart {
  line: string | null;
  rewrite: boolean;
  anchor: ViewAnchor | null;
  /** Scroll to the line without selecting it: Show in text on a passage whose
   *  skipped words are not known opens there, and selects nothing it cannot
   *  vouch for (ADR-0120). */
  noSelect?: boolean;
}

/** What is kept on the device until it is saved (ADR-0051). */
interface KeptEdits {
  revision_id: string;
  body: string;
  edits: Omit<Field, "key">[];
}

type SaveState = "idle" | "saving" | "saved" | "failed";

interface Rewriting {
  key: string;
  start: number;
  passage: string;
  before: string;
  after: string;
}

const keptKey = (chapterID: string) => `sageleaf.edit.${chapterID}`;

let nextKey = 0;
const newKey = () => `f${++nextKey}`;

/**
 * ChapterWorkbench is the studio's one edit mode (ADR-0051): the chapter's
 * text and its pictures, changed together, in place.
 *
 * Every paragraph is a box to type into, with its pictures and the gap for a
 * new one beneath it — the page as it will read, not a textarea in one place
 * and a picture list in another. Everything that ends the mode or acts on the
 * text lives in a bar pinned to the bottom, under the thumb, so nothing needs
 * a scroll to the top.
 *
 * Text is saved as the author goes: when a box is left, before any picture is
 * placed or moved, when the page is hidden, and on Done. Never on a timer — a
 * half-typed sentence is not a revision. Until a save lands the words are kept
 * on the device, with the revision they were typed over, so a reload loses
 * nothing and never writes old words over newer ones without asking.
 *
 * Writes run one at a time. A picture is anchored to a paragraph of the
 * *saved* text, so the index a picture action sends is only right once the
 * text before it has been saved.
 */
export default function ChapterWorkbench({
  chapterID,
  bookID,
  body,
  revisionID,
  canDescribe,
  canIllustrate,
  canRewrite,
  start,
  onDone,
}: {
  chapterID: string;
  bookID: string;
  /** The working text as last saved, and its revision. */
  body: string;
  revisionID: string;
  /** May spend on describing and drawing pictures (`chapter.illustrate`). */
  canDescribe: boolean;
  // canIllustrate false is a linked book: its pictures are the source's and
  // every write to one is refused (ADR-0098 §1). The workbench gates them
  // itself rather than relying on being unreachable, because the text entries
  // that used to gate it are now open — one change in two places, and this is
  // the second (ADR-0093's *Since*).
  canIllustrate: boolean;
  /** May ask for an AI rewrite (`chapter.draft`). */
  canRewrite: boolean;
  start: EditStart;
  /** Leave the mode, with where the author was. */
  onDone: (anchor: ViewAnchor | null) => void;
}) {
  const t = useT();
  const client = useQueryClient();

  const saved = useRef({ body, revisionID });
  const fieldsRef = useRef<Field[]>([]);
  const [fields, setFieldsState] = useState<Field[]>(() => {
    fieldsRef.current = fieldsOf(body, newKey);
    return fieldsRef.current;
  });
  const setFields = useCallback((next: Field[]) => {
    fieldsRef.current = next;
    setFieldsState(next);
  }, []);

  const [state, setState] = useState<SaveState>("idle");
  const [saveError, setSaveError] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [staleCopy, setStaleCopy] = useState<KeptEdits | null>(null);

  const [selection, setSelection] = useState<{ key: string; start: number; end: number } | null>(
    null,
  );
  const [rewriting, setRewriting] = useState<Rewriting | null>(null);
  // A range to select once the box is on screen and nothing covers it.
  const pendingSelection = useRef<{ key: string; start: number; end: number } | null>(null);

  // Which gap the picture sheet is open on: after a field, or at the top.
  const [adding, setAdding] = useState<{ key: string | null; index: number } | null>(null);
  // A placed picture being swapped for another in the same place (ADR-0053).
  const [replacing, setReplacing] = useState<ChapterImage | null>(null);
  const [moving, setMoving] = useState<ChapterImage | null>(null);
  const [moveError, setMoveError] = useState<unknown>(null);

  const images = useQuery({
    queryKey: ["chapter-images", chapterID, "draft"],
    queryFn: () => listChapterImages(chapterID, { draft: true }),
  });
  const refreshImages = useCallback(
    () => client.invalidateQueries({ queryKey: ["chapter-images", chapterID] }),
    [client, chapterID],
  );

  // One write at a time, in the order they were asked for.
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const run: Run = useCallback((write) => {
    const next = queue.current.then(write);
    queue.current = next.catch(() => undefined);
    return next;
  }, []);

  const keep = useCallback(() => {
    const { body: base, revisionID: revision } = saved.current;
    const current = fieldsRef.current;
    const paragraphs = paragraphsOf(base);
    const edits = current
      .filter((f) => isDirty(base, paragraphs, f))
      .map(({ from, to, text }) => ({ from, to, text }));
    try {
      if (edits.length === 0) {
        localStorage.removeItem(keptKey(chapterID));
      } else {
        const kept: KeptEdits = {
          revision_id: revision,
          body: applyFields(base, current),
          edits,
        };
        localStorage.setItem(keptKey(chapterID), JSON.stringify(kept));
      }
    } catch {
      // Storage full or refused: the save itself is still coming.
    }
  }, [chapterID]);

  /**
   * save writes every edited box into the saved text, then brings the boxes
   * and the pictures under them in line with what was saved. Throws what the
   * server said when it refused.
   */
  const saveNow = useCallback(async (): Promise<void> => {
    const base = saved.current.body;
    const sent = fieldsRef.current;
    const next = applyFields(base, sent);
    if (next === base) return;
    const snapshot = snapshotOf(base, sent);
    // From the cache, not from the last render: the save before this one
    // refetched the pictures, and nothing has rendered since.
    const before =
      client.getQueryData<ChapterImages>(["chapter-images", chapterID, "draft"])?.items ?? [];

    setState("saving");
    let result;
    try {
      result = await setChapterText(chapterID, next);
    } catch (err) {
      setSaveError(err);
      setState("failed");
      throw err;
    }
    saved.current = { body: result.body, revisionID: result.revision_id ?? "" };

    const focused = document.activeElement?.getAttribute("data-field-key") ?? null;
    const out = afterSave(fieldsRef.current, snapshot, result.body, focused, newKey);
    setFields(out?.fields ?? fieldsOf(result.body, newKey));

    // A picture under an edited paragraph lost its anchor with the old words.
    // It follows the paragraph to its new words — the author edited it in
    // place, they did not replace it. A move that fails leaves it unplaced,
    // which says so on the page.
    for (const image of before) {
      if (!image.placed) continue;
      const to = out?.follows.get(image.paragraph);
      if (to === undefined || to === null) continue;
      try {
        await updateImage(image.id, { paragraph: to });
      } catch {
        // Unplaced is the safe state (ADR-0038).
      }
    }

    keep();
    await Promise.all([
      client.invalidateQueries({ queryKey: ["chapter", chapterID] }),
      refreshImages(),
      client.invalidateQueries({ queryKey: ["estimate", chapterID] }),
      client.invalidateQueries({ queryKey: ["chapters", bookID] }),
      client.invalidateQueries({ queryKey: ["edition", bookID] }),
    ]);
    setSaveError(null);
    // Something may have been typed while this was on its way.
    const still = applyFields(saved.current.body, fieldsRef.current) !== saved.current.body;
    setState(still ? "idle" : "saved");
  }, [bookID, chapterID, client, keep, refreshImages, setFields]);

  /** save queues a save and says whether it worked; the bar says why not. */
  const save = useCallback(
    () =>
      run(saveNow).then(
        () => true,
        () => false,
      ),
    [run, saveNow],
  );

  // Words kept from a visit that never saved them. Put back over the same
  // revision; asked about over any other.
  const restored = useRef(false);
  useEffect(() => {
    if (restored.current) return;
    restored.current = true;
    let kept: KeptEdits | null = null;
    try {
      const raw = localStorage.getItem(keptKey(chapterID));
      kept = raw ? (JSON.parse(raw) as KeptEdits) : null;
    } catch {
      kept = null;
    }
    if (!kept) return;
    if (kept.revision_id !== saved.current.revisionID) {
      if (kept.body !== saved.current.body) setStaleCopy(kept);
      return;
    }
    setFields(restore(fieldsRef.current, kept.edits, newKey));
    setNotice(t.edit.restored);
    void save();
    // Once, on opening.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Leaving the page or the app is a save too. Best effort: the copy on the
  // device is what makes it safe.
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === "hidden") void save();
    };
    document.addEventListener("visibilitychange", onHide);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      void save();
    };
  }, [save]);

  // Opened on a line: select it, and open the rewrite on it when asked.
  // Otherwise keep the words that were at the top of the screen there.
  useLayoutEffect(() => {
    if (start.line) {
      const found = locate(fieldsRef.current, start.line.trim(), { key: "", start: 0 });
      if (!found) {
        setNotice(t.edit.lineNotInText);
      } else if (start.rewrite) {
        openRewrite(found);
      } else if (!start.noSelect) {
        pendingSelection.current = found;
      }
      const box = found && document.querySelector(`[data-field-key="${found.key}"]`);
      if (box) box.scrollIntoView({ block: "center" });
      return;
    }
    if (start.anchor) showField(start.anchor);
    // Once, on opening.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    const target = pendingSelection.current;
    if (!target || rewriting) return;
    const box = document.querySelector<HTMLTextAreaElement>(`[data-field-key="${target.key}"]`);
    if (!box) return;
    pendingSelection.current = null;
    box.focus({ preventScroll: true });
    box.setSelectionRange(target.start, target.end);
    setSelection(target);
  }, [fields, rewriting]);

  // A selection made by dragging handles on a phone raises nothing on the
  // box, only `selectionchange` on the document. Read only while a box has
  // focus, so tapping a button — which blurs it — keeps the selection.
  useEffect(() => {
    const onChange = () => {
      const el = document.activeElement;
      const key = el?.getAttribute("data-field-key");
      if (!key || !(el instanceof HTMLTextAreaElement)) return;
      setSelection(
        el.selectionEnd > el.selectionStart
          ? { key, start: el.selectionStart, end: el.selectionEnd }
          : null,
      );
    };
    document.addEventListener("selectionchange", onChange);
    // `select` does not bubble; caught on the way down instead.
    document.addEventListener("select", onChange, true);
    return () => {
      document.removeEventListener("selectionchange", onChange);
      document.removeEventListener("select", onChange, true);
    };
  }, []);

  function openRewrite(at: { key: string; start: number; end: number }) {
    const current = fieldsRef.current;
    const i = current.findIndex((f) => f.key === at.key);
    const field = current[i];
    if (!field) return;
    // The chapter as it now stands, for the text around the passage.
    const offset = current.slice(0, i).reduce((n, f) => n + f.text.length + 1, 0);
    const around = contextOf(current.map((f) => f.text).join("\n"), {
      start: offset + at.start,
      end: offset + at.end,
    });
    setRewriting({
      key: at.key,
      start: at.start,
      passage: field.text.slice(at.start, at.end),
      ...around,
    });
  }

  const placeRewrite = (version: string, how: Placement) => {
    if (!rewriting) return;
    const found = locate(fieldsRef.current, rewriting.passage, rewriting);
    setRewriting(null);
    if (!found) {
      setNotice(t.edit.passageMoved);
      return;
    }
    const field = fieldsRef.current.find((f) => f.key === found.key)!;
    const out = place(field.text, found, version, how);
    setFields(fieldsRef.current.map((f) => (f.key === found.key ? { ...f, text: out.body } : f)));
    keep();
    // In the box with the caret on it: saved when the author moves on, like
    // anything typed.
    pendingSelection.current = { key: found.key, ...out.selection };
  };

  const typed = (key: string, text: string) => {
    setFields(fieldsRef.current.map((f) => (f.key === key ? { ...f, text } : f)));
    keep();
    if (state === "saved" || state === "failed") setState("idle");
  };

  const left = (field: Field) => {
    const paragraphs = paragraphsOf(saved.current.body);
    const now = fieldsRef.current.find((f) => f.key === field.key);
    if (now && isDirty(saved.current.body, paragraphs, now)) void save();
  };

  /** The paragraph a picture in the gap after this field follows, once saved. */
  const slotAfter = (key: string | null, fallback: number) => {
    if (key === null) return TOP;
    const field = fieldsRef.current.find((f) => f.key === key);
    return field ? pictureSlot(field) : fallback;
  };

  const gap = async (key: string | null) => {
    if (moving) {
      const image = moving;
      setMoveError(null);
      try {
        await run(async () => {
          await saveNow();
          await updateImage(image.id, { paragraph: slotAfter(key, TOP) });
          await refreshImages();
        });
        setMoving(null);
      } catch (err) {
        setMoveError(err);
      }
      return;
    }
    if (!(await save())) return;
    setAdding({ key, index: slotAfter(key, TOP) });
  };

  const replace = async (image: ChapterImage) => {
    // Saved first, so the paragraph the brief is written from is the one the
    // server holds.
    if (!(await save())) return;
    setReplacing(image);
  };

  const done = async () => {
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    if (!(await save())) return;
    const anchor = fieldAtTop();
    try {
      localStorage.removeItem(keptKey(chapterID));
    } catch {
      // Nothing kept, or nothing to remove.
    }
    onDone(anchor);
  };

  const applyStaleCopy = async () => {
    const copy = staleCopy;
    if (!copy) return;
    setStaleCopy(null);
    await run(async () => {
      try {
        const result = await setChapterText(chapterID, copy.body);
        saved.current = { body: result.body, revisionID: result.revision_id ?? "" };
        setFields(fieldsOf(result.body, newKey));
        localStorage.removeItem(keptKey(chapterID));
        await Promise.all([
          client.invalidateQueries({ queryKey: ["chapter", chapterID] }),
          refreshImages(),
        ]);
        setState("saved");
      } catch (err) {
        setSaveError(err);
        setState("failed");
      }
    });
  };

  const discardStaleCopy = () => {
    setStaleCopy(null);
    try {
      localStorage.removeItem(keptKey(chapterID));
    } catch {
      // Nothing to remove.
    }
  };

  const placedAt = (index: number) =>
    (images.data?.items ?? []).filter((i) => i.placed && i.paragraph === index);
  const unplaced = (images.data?.items ?? []).filter((i) => !i.placed);
  const cast = images.data?.cast;
  const blocked = !!cast && !cast.ready;

  const paragraphs = paragraphsOf(saved.current.body);
  const dirty = fields.some((f) => isDirty(saved.current.body, paragraphs, f));
  const subject =
    adding && adding.key !== null ? (fields.find((f) => f.key === adding.key)?.text ?? "") : "";

  const picture = (image: ChapterImage) => (
    <Placed
      key={image.id}
      chapterID={chapterID}
      image={image}
      onMove={setMoving}
      onReplace={(image) => void replace(image)}
      onChanged={refreshImages}
      run={run}
      canEdit={canIllustrate}
    />
  );

  return (
    <section data-testid="chapter-editor">
      {notice && (
        <p
          data-testid="edit-notice"
          className="mb-3 rounded-control bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
        >
          {notice}
        </p>
      )}

      {staleCopy && (
        <div
          data-testid="edit-stale-copy"
          className="mb-4 rounded-card border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200"
        >
          <p>{t.edit.staleCopy}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              data-testid="edit-stale-use"
              onClick={() => void applyStaleCopy()}
              className="rounded-control border border-amber-400 px-3 py-1 text-xs"
            >
              {t.edit.useStaleCopy}
            </button>
            <button
              type="button"
              data-testid="edit-stale-discard"
              onClick={discardStaleCopy}
              className="rounded-control px-3 py-1 text-xs"
            >
              {t.edit.discardStaleCopy}
            </button>
          </div>
        </div>
      )}

      <CastNotices cast={cast} bookID={bookID} />

      {/* The whole-chapter run (ADR-0074), beside the per-paragraph + rather
          than instead of it. */}
      {canIllustrate && (
        <IllustrateChapter
          chapterID={chapterID}
          bookID={bookID}
          estimates={images.data?.run_estimates}
          canDraw={!!cast?.ready}
          onDrawn={() => void refreshImages()}
        />
      )}

      {moving && (
        <p
          data-testid="image-moving"
          className="sticky top-2 z-10 mb-3 flex items-center justify-between gap-2 rounded-control bg-sage-soft px-3 py-2 text-sm text-sage-deep"
        >
          {t.images.moving}
          <button
            type="button"
            data-testid="image-move-cancel"
            onClick={() => setMoving(null)}
            className="rounded-control border border-sage px-2 py-0.5 text-xs"
          >
            {t.images.cancelMove}
          </button>
        </p>
      )}
      {/* A refused move leaves the chapter in "choose a place" mode with the
          picture where it was — confusing until somebody says why. */}
      <MutationError
        className="mb-3"
        testId="image-move-error"
        error={moveError}
        fallback={t.images.moveFailed}
      />

      {images.isError && (
        <p role="alert" className="mb-3 text-sm text-red-600 dark:text-red-400">
          {t.images.loadFailed}
        </p>
      )}

      {canIllustrate && (
        <Inserter
          label={t.images.addAtTop}
          onClick={() => void gap(null)}
          moving={!!moving}
          disabled={blocked}
        />
      )}
      {placedAt(TOP).map(picture)}

      {fields.map((field, i) => (
        <div key={field.key}>
          <ParagraphBox
            field={field}
            label={t.edit.paragraph(i + 1)}
            onChange={(text) => typed(field.key, text)}
            onLeave={() => left(field)}
          />
          {range(field.from, field.to).flatMap(placedAt).map(picture)}
          {canIllustrate && (
            <Inserter
              label={t.images.addHere}
              onClick={() => void gap(field.key)}
              moving={!!moving}
              disabled={blocked}
            />
          )}
        </div>
      ))}

      {canIllustrate && (
        <UnplacedPictures
          chapterID={chapterID}
          images={unplaced}
          onMove={setMoving}
          onReplace={(image) => void replace(image)}
          onChanged={refreshImages}
          run={run}
        />
      )}

      {/* Room for the bar, so the end of the chapter is not under it. */}
      <div aria-hidden="true" className="h-32" />

      <div
        data-testid="edit-bar"
        className="fixed inset-x-0 bottom-0 z-20 border-t border-line bg-ground/95 backdrop-blur-md"
      >
        <div className="mx-auto max-w-3xl px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          {state === "failed" && (
            <div
              role="alert"
              data-testid="save-text-error"
              className="mb-2 flex flex-wrap items-center gap-2 text-sm text-red-600 dark:text-red-400"
            >
              <MutationError error={saveError} fallback={t.edit.saveFailed} />
              <button
                type="button"
                data-testid="save-text-retry"
                onClick={() => void save()}
                className="rounded-control border border-current px-2 py-0.5 text-xs"
              >
                {t.edit.retry}
              </button>
            </div>
          )}
          <div className="flex items-center gap-2">
            {canRewrite && (
              <button
                type="button"
                data-testid="rewrite-selection"
                disabled={!selection}
                // Keeps the box's selection on a desktop, where pressing a
                // button would otherwise collapse it before the click lands.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => selection && openRewrite(selection)}
                className="shrink-0 rounded-control border border-sage px-3 py-1.5 text-sm font-medium text-sage-deep transition-colors hover:bg-sage-soft disabled:opacity-40"
              >
                {t.edit.rewriteSelection}
              </button>
            )}
            <p
              data-testid="save-state"
              data-state={state === "idle" && dirty ? "dirty" : state}
              aria-live="polite"
              className="min-w-0 flex-1 truncate text-xs text-muted"
            >
              {state === "saving"
                ? t.edit.saving
                : state === "failed"
                  ? ""
                  : dirty
                    ? t.edit.unsaved
                    : state === "saved"
                      ? t.edit.saved
                      : t.edit.savesAsYouGo}
            </p>
            <button
              type="button"
              data-testid="edit-done"
              // Never disabled while saving: pressing it blurs the paragraph,
              // which starts a save, and a button disabled by that save
              // swallows the press. Done waits for the save anyway.
              onClick={() => void done()}
              className="shrink-0 rounded-control bg-sage px-4 py-1.5 text-sm font-medium text-on-sage transition-colors hover:bg-sage-deep disabled:opacity-40"
            >
              {t.edit.done}
            </button>
          </div>
          {canRewrite && !selection && (
            <p className="mt-1.5 text-xs text-muted">{t.edit.selectToRewrite}</p>
          )}
        </div>
      </div>

      {rewriting && (
        <RewriteSheet
          chapterID={chapterID}
          passage={rewriting.passage}
          before={rewriting.before}
          after={rewriting.after}
          onPlace={placeRewrite}
          onClose={() => setRewriting(null)}
        />
      )}

      {adding && (
        <PictureSheet
          kind="scene"
          title={adding.key === null ? t.images.titleTop : t.images.title}
          subject={subject}
          prices={images.data?.draw_prices ?? []}
          canDescribe={canDescribe}
          onDescribe={(v) =>
            describeImage(chapterID, { paragraph: slotAfter(adding.key, adding.index), ...v })
          }
          onGenerate={(prompt, shape, who, beside, quality, forms) =>
            generateImage(chapterID, {
              prompt,
              shape,
              quality,
              character_ids: who,
              form_ids: forms,
              reference_image_ids: beside,
            })
          }
          castBookID={bookID}
          onUpload={(file) => uploadImage(chapterID, file)}
          onCrop={(key, region) => cropImage(chapterID, key, region)}
          charactersHref={`/studio/books/${bookID}/characters`}
          onUse={(candidate) =>
            run(async () => {
              await saveNow();
              await placeImage(chapterID, {
                object_key: candidate.object_key,
                paragraph: slotAfter(adding.key, adding.index),
                prompt: candidate.prompt,
                source: candidate.prompt ? "generated" : "upload",
                quality: candidate.quality,
                character_ids: candidate.character_ids,
                form_ids: candidate.form_ids,
                reference_image_ids: candidate.reference_image_ids,
              });
              setAdding(null);
              await refreshImages();
            })
          }
          onClose={() => setAdding(null)}
        />
      )}

      {replacing && (
        <PictureSheet
          kind="scene"
          title={t.images.replaceTitle}
          subject={
            paragraphs.find((p) => replacing.placed && p.index === replacing.paragraph)?.text ??
            replacing.anchor_text ??
            ""
          }
          prices={images.data?.draw_prices ?? []}
          canDescribe={canDescribe}
          onDescribe={(v) =>
            describeImage(chapterID, {
              paragraph: replacing.placed ? replacing.paragraph : TOP,
              ...v,
            })
          }
          onGenerate={(prompt, shape, who, beside, quality, forms) =>
            generateImage(chapterID, {
              prompt,
              shape,
              quality,
              character_ids: who,
              form_ids: forms,
              reference_image_ids: beside,
            })
          }
          castBookID={bookID}
          onUpload={(file) => uploadImage(chapterID, file)}
          onCrop={(key, region) => cropImage(chapterID, key, region)}
          charactersHref={`/studio/books/${bookID}/characters`}
          current={replacing}
          onUse={(candidate) =>
            run(async () => {
              // Always a whole replacement: a crop of the picture in force
              // comes back carrying its own words and cast, so it keeps them.
              await updateImage(replacing.id, {
                object_key: candidate.object_key,
                prompt: candidate.prompt ?? "",
                source: candidate.prompt ? "generated" : "upload",
                character_ids: candidate.character_ids ?? [],
                form_ids: candidate.form_ids ?? [],
                reference_image_ids: candidate.reference_image_ids,
              });
              setReplacing(null);
              await refreshImages();
            })
          }
          onClose={() => setReplacing(null)}
        />
      )}
    </section>
  );
}

function range(from: number, to: number): number[] {
  return Array.from({ length: Math.max(0, to - from) }, (_, i) => from + i);
}

/**
 * ParagraphBox is one paragraph, editable where it stands.
 *
 * Styled as the reading text rather than as a form field, so the edit mode
 * still reads as the chapter; the box shows itself on focus. A line break
 * typed here becomes a new paragraph when it is saved.
 */
function ParagraphBox({
  field,
  label,
  onChange,
  onLeave,
}: {
  field: Field;
  label: string;
  onChange: (text: string) => void;
  onLeave: () => void;
}) {
  // Grown to its text: a box that scrolls inside a scrolling page is the one
  // thing a paragraph editor must not do.
  const box = useGrowToText(field.text);

  return (
    <textarea
      ref={box}
      // On the box and nowhere inside it: each paragraph finds its own
      // direction from its own first strong character (CLAUDE.md).
      dir="auto"
      rows={1}
      value={field.text}
      aria-label={label}
      data-testid="paragraph-field"
      data-field-key={field.key}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onLeave}
      className="reading-text -mx-2 block w-[calc(100%+1rem)] resize-none overflow-hidden rounded-control border border-transparent bg-transparent px-2 py-1 transition-colors hover:bg-sunk/60 focus:border-line focus:bg-surface focus:outline-none"
    />
  );
}
